import { useAuth } from "@/context/AuthContext";
import API from "@/lib/axios";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { io, type Socket } from "socket.io-client";

type SocketFactory = typeof io;

// Optional WebSocket support - gracefully handle if socket.io-client not installed
const socketFactory: SocketFactory | null =
  typeof window === "undefined" ? null : io;

export interface AgentUser {
  id: number;
  name: string;
  email: string;
  roleName?: string | null;
  lastActivityAt: string | null;
  isOnline: boolean;
  unreadCount: number;
}

export interface ChatMessage {
  id: number;
  sender_id: number;
  receiver_id: number;
  message: string;
  status: "sent" | "delivered" | "read";
  created_at: string;
  read_at: string | null;
}

const SOCKET_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5001";

export const useAgentChat = () => {
  const { user } = useAuth();
  
  // Get token from localStorage since it's not in AuthContext
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("authToken");
  });
  
  // Update token when user changes (login/logout)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedToken = localStorage.getItem("authToken");
    setToken(storedToken);
  }, [user]);

  const [agents, setAgents] = useState<AgentUser[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPollingMessages, setIsPollingMessages] = useState(false);
  
  // WebSocket support with fallback
  const socketRef = useRef<Socket | null>(null);
  const [useWebSocket, setUseWebSocket] = useState(Boolean(socketFactory)); // Only try WebSocket if available
  const [wsConnected, setWsConnected] = useState(false);

  const fetchAgents = useCallback(async () => {
    setIsLoadingAgents(true);
    setAgentsError(null);
    try {
      const res = await API.get("/api/v1/chat/agents");
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setAgents(data);

      if (!selectedAgentId && data.length > 0) {
        setSelectedAgentId(data[0].id);
      } else if (selectedAgentId && !data.some((agent: AgentUser) => agent.id === selectedAgentId)) {
        setSelectedAgentId(data.length ? data[0].id : null);
      }
    } catch (error: unknown) {
      console.error("Failed to fetch agents:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      setAgentsError(axiosError.response?.data?.message || "Failed to load agents");
    } finally {
      setIsLoadingAgents(false);
    }
  }, [selectedAgentId]);

  const fetchMessages = useCallback(async () => {
    if (!selectedAgentId) {
      return;
    }
    const isFirstLoad = !isPollingMessages;
    if (isFirstLoad) {
      setIsLoadingMessages(true);
    } else {
      setIsPollingMessages(true);
    }
    setMessagesError(null);
    try {
      const res = await API.get("/api/v1/chat/messages", {
        params: { peerId: selectedAgentId, limit: 200 },
      });
      setMessages(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error: unknown) {
      console.error("Failed to fetch messages:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      setMessagesError(axiosError.response?.data?.message || "Failed to load messages");
    } finally {
      if (isFirstLoad) {
        setIsLoadingMessages(false);
      }
      setIsPollingMessages(false);
    }
  }, [selectedAgentId, isPollingMessages]);

  // Initialize WebSocket (optional, falls back to polling)
  useEffect(() => {
    if (!useWebSocket || !token || !user || !socketFactory) return;

    // Try to create WebSocket connection
    const socket = socketFactory(SOCKET_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 3,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ WebSocket connected for agent chat");
      setWsConnected(true);
    });

    socket.on("disconnect", () => {
      console.log("❌ WebSocket disconnected, falling back to polling");
      setWsConnected(false);
      setUseWebSocket(false); // Disable WebSocket, use polling
    });

    socket.on("connect_error", () => {
      console.log("⚠️ WebSocket connection failed, using polling");
      setUseWebSocket(false); // Fallback to polling
      socket.disconnect();
    });

    // Listen for real-time messages
    socket.on("agent:message:received", (message: ChatMessage) => {
      if (message.receiver_id === Number(user.id)) {
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
        // Refresh agents list to update unread count
        fetchAgents();
      }
    });

    socket.on("agent:message:sent", (message: ChatMessage) => {
      if (message.sender_id === Number(user.id)) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }
    });

    socket.on("agent:unread:update", () => {
      fetchAgents();
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setWsConnected(false);
    };
  }, [useWebSocket, token, user, fetchAgents]);

  // Fetch agents list (only on initial load and when WebSocket events trigger)
  useEffect(() => {
    fetchAgents();
    // Removed continuous polling - only fetch when WebSocket events occur
  }, [fetchAgents]);

  // Fetch messages (only on initial load and when WebSocket events trigger)
  useEffect(() => {
    if (!selectedAgentId) {
      setMessages([]);
      return;
    }
    
    // Initial load only
    fetchMessages();
    
    // Removed continuous polling - only fetch when WebSocket events occur
    // If WebSocket is not connected, user will need to manually refresh or wait for WebSocket to connect
  }, [selectedAgentId, fetchMessages]);

  const handleSendMessage = useCallback(async () => {
    if (!selectedAgentId || !messageInput.trim()) {
      return;
    }
    
    const messageText = messageInput.trim();
    setMessageInput("");
    setIsSending(true);

    // Try WebSocket first if connected
    if (wsConnected && socketRef.current) {
      try {
        socketRef.current.emit("agent:message:send", {
          receiverId: selectedAgentId,
          message: messageText,
        });
        // Optimistically add message (will be confirmed by socket event)
        setIsSending(false);
        return;
      } catch (error) {
        console.error("WebSocket send failed, falling back to API:", error);
        // Fall through to API fallback
      }
    }

    // Fallback to REST API (always works)
    try {
      const res = await API.post("/api/v1/chat/messages", {
        receiverId: selectedAgentId,
        message: messageText,
      });
      const created = res.data?.data;
      if (created) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === created.id)) return prev;
          return [...prev, created];
        });
      }
      fetchAgents();
    } catch (error: unknown) {
      console.error("Failed to send message:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      toast.error(axiosError.response?.data?.message || "Unable to send message");
    } finally {
      setIsSending(false);
    }
  }, [selectedAgentId, messageInput, wsConnected, fetchAgents]);

  const filteredAgents = useMemo(() => {
    if (!searchTerm.trim()) {
      return agents;
    }
    return agents.filter((agent) =>
      agent.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [agents, searchTerm]);

  return {
    user,
    agents,
    filteredAgents,
    isLoadingAgents,
    agentsError,
    selectedAgentId,
    setSelectedAgentId,
    messages,
    isLoadingMessages,
    messagesError,
    messageInput,
    setMessageInput,
    isSending,
    searchTerm,
    setSearchTerm,
    handleSendMessage,
    refetchAgents: fetchAgents,
    refetchMessages: fetchMessages,
  };
};

